import React, { useState, useRef, useEffect } from 'react';
import {
  ChatPanelOverlay,
  ChatPanelContainer,
  ChatHeader,
  ChatTitle,
  CloseButton,
  ChatMessagesContainer,
  MessageBubble,
  MessageTime,
  TypingIndicator,
  ThinkingText,
  ChatInputContainer,
  ChatInputWrapper,
  ChatInput,
  SendButton,
  SuggestionsContainer,
  SuggestionChip,
  EmptyStateContainer,
  WelcomeCard,
} from './styled';
import { ChatPanelProps } from './types';
import { MessageFormatter } from './MessageFormatter';
import CopilotLogo from 'shared/images/vero-copilot-logo.svg';

const REASONING_STEPS = [
  'Analyzing Lumen data',
  'Cross-referencing applicant history',
  'Reviewing income verification',
  'Checking credit scores',
  'Evaluating criminal records',
  'Assessing rental history',
  'Calculating risk factors',
  'Synthesizing insights',
];

const formatTime = (date: Date) => {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const SUGGESTIONS = [
  "Is there anything unusual or inconsistent in this application?",
  "Why did Lumen score this the way it did?",
  "What documentation would strengthen this application?",
  "What objective criteria support or oppose approval?",
  "How should I interpret this criminal record in a housing context?",
  "What's the meaningful story in the credit report?",
  "What's the margin of safety on this income-to-rent ratio?",
  "Are there signs of financial stress in the credit report?",
];

export const VeroCopilotPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  messages,
  onSendMessage,
  onClearMessages,
  isLoading,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [reasoningStep, setReasoningStep] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cycle through reasoning steps while loading
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setReasoningStep((prev) => (prev + 1) % REASONING_STEPS.length);
      }, 2000); // Change every 2 seconds
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (!isLoading) {
      onSendMessage(suggestion);
    }
  };

  const handleBackToSuggestions = () => {
    onClearMessages();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const showSuggestions = messages.length === 0 && !isLoading;
  const showBackButton = messages.length > 0;

  return (
    <>
      <ChatPanelOverlay isOpen={isOpen} onClick={onClose} />
      <ChatPanelContainer isOpen={isOpen} onKeyDown={handleKeyDown}>
        <ChatHeader>
          <ChatTitle>
            {showBackButton && (
              <button
                onClick={handleBackToSuggestions}
                style={{
                  background: 'rgba(107, 114, 128, 0.1)',
                  border: '1px solid rgba(107, 114, 128, 0.2)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '8px',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#374151',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.15)';
                  e.currentTarget.style.transform = 'translateX(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
                aria-label="Back to suggestions"
                title="Back to suggestions"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19 12H5M5 12L12 19M5 12L12 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <img
              src={CopilotLogo}
              alt="VERO Copilot"
              style={{ height: '44px', width: 'auto' }}
            />
            <div className="status-indicator" />
          </ChatTitle>
          <CloseButton onClick={onClose} aria-label="Close chat">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </CloseButton>
        </ChatHeader>

        <ChatMessagesContainer>
          {messages.length === 0 && !isLoading && (
            <WelcomeCard>
              <h2>Welcome to Copilot</h2>
              <p>VERO's AI Property Analyst</p>
              <p className="description">
                I can help you understand application information faster, simulate changes in information, and much more.
              </p>
            </WelcomeCard>
          )}

          {messages.map((message) => (
            <MessageBubble key={message.id} sender={message.sender}>
              {message.sender === 'assistant' ? (
                <MessageFormatter content={message.content} />
              ) : (
                <p>{message.content}</p>
              )}
              <MessageTime>{formatTime(message.timestamp)}</MessageTime>
            </MessageBubble>
          ))}

          {isLoading && (
            <TypingIndicator>
              <ThinkingText>{REASONING_STEPS[reasoningStep]}</ThinkingText>
            </TypingIndicator>
          )}

          <div ref={messagesEndRef} />
        </ChatMessagesContainer>

        {showSuggestions && (
          <SuggestionsContainer>
            {SUGGESTIONS.map((suggestion) => (
              <SuggestionChip
                key={suggestion}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </SuggestionChip>
            ))}
          </SuggestionsContainer>
        )}

        <ChatInputContainer>
          <form onSubmit={handleSubmit}>
            <ChatInputWrapper>
              <ChatInput
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask about this application..."
                disabled={isLoading}
              />
              <SendButton
                type="submit"
                disabled={!inputValue.trim() || isLoading}
                aria-label="Send message"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 2L11 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </SendButton>
            </ChatInputWrapper>
          </form>
        </ChatInputContainer>
      </ChatPanelContainer>
    </>
  );
};
